import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Wallet, TrendingUp, TrendingDown, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  quantPaperAccount, quantPaperStats, quantPaperTrades, getErrorMessage,
} from '../../lib/api';
import PositionsTable from './PositionsTable';
import AutoTraderPanel from './AutoTraderPanel';
import PerformanceLiveCard from './PerformanceLiveCard';

/**
 * Tab "Portfolio" del Workbench.
 * Mostra: account snapshot, posizioni aperte, stats per strategia, trades recenti.
 */

const fmtUSD = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtPct = (v) => {
  if (v == null) return '—';
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fmtPctDirect = (v) => {
  // Valore già in percentuale (es. win_rate = 0.65 → 65%)
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
};
const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' });
};

// KPI card dell'account snapshot
const AccountKPI = ({ label, value, sub, highlight }) => (
  <div className="rounded-lg bg-muted/30 px-4 py-3">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold tabular-nums mt-0.5 ${highlight}`}>{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const PortfolioTab = () => {
  // Account snapshot
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [providerMissing, setProviderMissing] = useState(false);
  const accountTimerRef = useRef(null);

  // Stats per strategia
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Trades recenti
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Segnale per forzare refetch PositionsTable dopo operazioni
  const [positionsRefreshSignal, setPositionsRefreshSignal] = useState(null);

  // Lista strategie disponibili (derivata dai trades)
  const [availableStrategies, setAvailableStrategies] = useState([]);

  // --- Account polling ---
  const fetchAccount = useCallback(async () => {
    setAccountLoading(true);
    setAccountError(null);
    try {
      const r = await quantPaperAccount();
      setAccount(r.data);
      setProviderMissing(false);
    } catch (e) {
      if (e.response?.status === 503) {
        setProviderMissing(true);
        setAccount(null);
      } else {
        setAccountError(getErrorMessage(e, 'Errore account'));
      }
    } finally {
      setAccountLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
    accountTimerRef.current = setInterval(fetchAccount, 30000);
    return () => clearInterval(accountTimerRef.current);
  }, [fetchAccount]);

  // --- Stats ---
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await quantPaperStats();
      const raw = r.data?.strategies ?? r.data;
      setStats(Array.isArray(raw) ? raw : []);
    } catch (e) {
      // Stats non bloccanti — silenzio in caso di 404/503
      if (e.response?.status !== 503 && e.response?.status !== 404) {
        toast.error(getErrorMessage(e, 'Errore stats strategia'));
      }
      setStats([]);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // --- Trades ---
  const fetchTrades = useCallback(async () => {
    setTradesLoading(true);
    try {
      const params = { limit: 30 };
      if (filterStrategy !== 'all') params.strategy = filterStrategy;
      if (filterStatus !== 'all') params.status = filterStatus;
      const r = await quantPaperTrades(params);
      const raw = r.data?.trades ?? r.data;
      const items = Array.isArray(raw) ? raw : [];
      setTrades(items);

      // Ricava lista strategie uniche per il filtro
      const strats = [...new Set(items.map((t) => t.strategy).filter(Boolean))];
      setAvailableStrategies(strats);
    } catch (e) {
      if (e.response?.status !== 503 && e.response?.status !== 404) {
        toast.error(getErrorMessage(e, 'Errore trades'));
      }
      setTrades([]);
    } finally {
      setTradesLoading(false);
    }
  }, [filterStrategy, filterStatus]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  // P&L Today: confronta equity attuale con equity ieri
  const equityNum = account?.equity != null ? Number(account.equity) : null;
  const lastEquityNum = account?.last_equity != null ? Number(account.last_equity) : null;
  const pnlToday = equityNum != null && lastEquityNum != null ? equityNum - lastEquityNum : null;
  const pnlTodayPct = lastEquityNum != null && lastEquityNum > 0 && pnlToday != null
    ? (pnlToday / lastEquityNum) * 100 : null;

  const pnlColor =
    pnlToday == null ? '' : pnlToday >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';

  // Provider mancante — mostra banner globale
  if (providerMissing) {
    return (
      <Card className="border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20">
        <CardContent className="py-6 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Alpaca non configurato</p>
            <p className="text-muted-foreground mt-1 text-xs">
              Per usare il Portfolio paper trading configura il provider Alpaca.{' '}
              <a href="/admin/market-providers" className="text-primary hover:underline">
                Configura market provider
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── 0. Auto-trader ── */}
      <AutoTraderPanel />

      {/* ── 1. Account Snapshot ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              Account Paper · Snapshot
            </span>
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={fetchAccount} disabled={accountLoading}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${accountLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          {accountError ? (
            <p className="text-sm text-red-600">{accountError}</p>
          ) : accountLoading && !account ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted/40 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : account ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AccountKPI
                label="Equity"
                value={fmtUSD(account.equity)}
                sub={`Portfolio value`}
              />
              <AccountKPI
                label="Cash"
                value={fmtUSD(account.cash)}
                sub="Liquidità disponibile"
              />
              <AccountKPI
                label="Buying Power"
                value={fmtUSD(account.buying_power)}
                sub="Potere d'acquisto"
              />
              <AccountKPI
                label="P&L Oggi"
                value={
                  pnlToday != null
                    ? `${pnlToday >= 0 ? '+' : ''}$${Math.abs(pnlToday).toFixed(2)}`
                    : '—'
                }
                sub={pnlTodayPct != null ? `${pnlTodayPct >= 0 ? '+' : ''}${pnlTodayPct.toFixed(2)}%` : undefined}
                highlight={pnlColor}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Dati account non disponibili.</p>
          )}
        </CardContent>
      </Card>

      {/* ── 2. Posizioni aperte ── */}
      <section>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          Posizioni aperte
        </h3>
        <PositionsTable refreshSignal={positionsRefreshSignal} />
      </section>

      {/* ── 3. Stats per strategia ── */}
      <section>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          Stats per strategia
          {statsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </h3>

        {!statsLoading && (!stats || stats.length === 0) ? (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Nessuna statistica disponibile. I dati appaiono dopo il primo trade chiuso.
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Strategia</TableHead>
                  <TableHead className="text-right text-xs"># Trades</TableHead>
                  <TableHead className="text-right text-xs">Aperti</TableHead>
                  <TableHead className="text-right text-xs">Chiusi</TableHead>
                  <TableHead className="text-right text-xs">Win Rate</TableHead>
                  <TableHead className="text-right text-xs">Avg P&amp;L %</TableHead>
                  <TableHead className="text-right text-xs">Profit Factor</TableHead>
                  <TableHead className="text-right text-xs">Avg Hold (gg)</TableHead>
                  <TableHead className="text-right text-xs">P&amp;L Totale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats || []).map((s) => (
                  <TableRow key={s.strategy}>
                    <TableCell>
                      <span className="font-mono text-xs">{s.strategy}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{s.n_trades ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{s.n_open ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{s.n_closed ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtPctDirect(s.win_rate)}</TableCell>
                    <TableCell className={`text-right tabular-nums text-sm font-medium ${
                      s.avg_pnl_pct == null ? 'text-muted-foreground'
                        : Number(s.avg_pnl_pct) >= 0 ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {fmtPct(s.avg_pnl_pct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtNum(s.profit_factor)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtNum(s.avg_holding_days, 1)}</TableCell>
                    <TableCell className={`text-right tabular-nums text-sm font-medium ${
                      s.total_pnl_abs == null ? 'text-muted-foreground'
                        : Number(s.total_pnl_abs) >= 0 ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {fmtUSD(s.total_pnl_abs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* PerformanceLiveCard per ogni strategia con posizioni aperte */}
        {(stats || []).filter((s) => (s.n_open ?? 0) > 0).length > 0 && (
          <div className="mt-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Performance live · strategie con posizioni aperte
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(stats || [])
                .filter((s) => (s.n_open ?? 0) > 0)
                .map((s) => (
                  <PerformanceLiveCard key={s.strategy} strategy={s.strategy} />
                ))}
            </div>
          </div>
        )}
      </section>

      {/* ── 4. Trades recenti ── */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-primary" />
            Trades recenti
            {tradesLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </h3>
          {/* Filtri */}
          <div className="flex gap-2 flex-wrap">
            <Select value={filterStrategy} onValueChange={setFilterStrategy}>
              <SelectTrigger className="h-8 text-xs w-44">
                <SelectValue placeholder="Strategia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le strategie</SelectItem>
                {availableStrategies.map((s) => (
                  <SelectItem key={s} value={s}><span className="font-mono">{s}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="open">Aperti</SelectItem>
                <SelectItem value="closed">Chiusi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!tradesLoading && trades.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Nessun trade trovato con i filtri correnti.
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Data</TableHead>
                  <TableHead className="text-xs font-mono">Symbol</TableHead>
                  <TableHead className="text-xs">Strategia</TableHead>
                  <TableHead className="text-xs">Lato</TableHead>
                  <TableHead className="text-right text-xs">Qty</TableHead>
                  <TableHead className="text-right text-xs">Entry</TableHead>
                  <TableHead className="text-right text-xs">Exit</TableHead>
                  <TableHead className="text-right text-xs">P&amp;L %</TableHead>
                  <TableHead className="text-xs">Stato</TableHead>
                  <TableHead className="text-xs">Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.map((t, idx) => {
                  const pnlPctN = t.pnl_pct != null ? Number(t.pnl_pct) : null;
                  return (
                    <TableRow key={t.id || idx}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {t.created_at
                          ? new Date(t.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })
                          : '—'}
                      </TableCell>
                      <TableCell className="font-mono text-sm font-semibold">{t.symbol}</TableCell>
                      <TableCell className="text-xs">
                        {t.strategy ? (
                          <span className="font-mono text-[10px] bg-muted rounded px-1 py-0.5">{t.strategy}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${t.side === 'buy' ? 'border-emerald-500 text-emerald-700' : 'border-red-500 text-red-700'}`}
                        >
                          {t.side?.toUpperCase() || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{t.qty ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums font-mono text-sm">{fmtUSD(t.entry_price)}</TableCell>
                      <TableCell className="text-right tabular-nums font-mono text-sm">
                        {t.exit_price != null ? fmtUSD(t.exit_price) : <span className="text-muted-foreground text-xs">aperto</span>}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums text-sm font-medium ${
                        pnlPctN == null ? 'text-muted-foreground'
                          : pnlPctN >= 0 ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {pnlPctN != null
                          ? `${pnlPctN >= 0 ? '+' : ''}${(pnlPctN * 100).toFixed(2)}%`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={`text-[10px] ${
                            t.status === 'closed' ? 'bg-muted text-muted-foreground'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          }`}
                        >
                          {t.status || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">
                        {t.close_reason || t.reason || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PortfolioTab;
